/**
 * TimeZonePicker Component
 *
 * Lets a public booker read every time in THEIR zone. Rendered next to the
 * times (sidebar + slot step) because a timezone control the booker never sees
 * is the same bug as having no control at all.
 *
 * Deliberately a NATIVE `<select>`: it needs no portal (so it cannot be clipped
 * or land under an overlay on a public page), it gets the OS wheel picker on
 * mobile, and it is keyboard/screen-reader correct with no extra work.
 */

import React, { useId, useMemo } from 'react';
import { Globe } from 'lucide-react';
import {
  formatTimeZoneAbbreviation,
  getGroupedTimeZoneOptions,
} from '../utils/bookingTimezones.js';

interface TimeZonePickerProps {
  /** Currently selected IANA zone. */
  value: string;
  onChange: (zone: string) => void;
  /**
   * Instant used to resolve DST-sensitive abbreviations. Pass the selected
   * slot's start when there is one so the label matches the meeting, not today.
   */
  referenceDate?: Date;
  /** Hide the "Time zone" caption when the surrounding block already says it. */
  hideLabel?: boolean;
  className?: string;
}

export function TimeZonePicker({
  value,
  onChange,
  referenceDate,
  hideLabel = false,
  className,
}: TimeZonePickerProps) {
  const selectId = useId();
  const groups = useMemo(() => getGroupedTimeZoneOptions(value), [value]);
  const abbreviation = formatTimeZoneAbbreviation(value, referenceDate);

  return (
    <div className={className}>
      {!hideLabel && (
        <label
          htmlFor={selectId}
          className="mb-1.5 flex items-center gap-2 text-sm font-medium text-gray-700"
        >
          <Globe className="h-4 w-4 shrink-0 text-blue-600" aria-hidden />
          Time zone
        </label>
      )}
      <div className="relative">
        <select
          id={selectId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label="Time zone for displayed times"
          className="w-full appearance-none rounded-lg border border-gray-300 bg-card py-2 pl-3 pr-16 text-sm text-card-foreground shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {groups.map((group) => (
            <optgroup key={group.region} label={group.region}>
              {group.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {/* The live abbreviation sits inside the control so the current offset
            is readable without opening the list. */}
        <span
          className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-semibold uppercase tracking-wide text-blue-700"
          aria-hidden
        >
          {abbreviation}
        </span>
      </div>
    </div>
  );
}
